import {
  STRUGGLE_ABANDON_COUNT,
  STRUGGLE_ABANDON_WINDOW_MS,
  STRUGGLE_API_ERROR_COUNT,
  STRUGGLE_API_ERROR_WINDOW_MS,
  STRUGGLE_BLOCKED_COUNT,
  STRUGGLE_BLOCKED_WINDOW_MS,
  STRUGGLE_BURST_MS,
  STRUGGLE_VALIDATION_COUNT,
  STRUGGLE_VALIDATION_WINDOW_MS,
} from '../timing';
import type { InlineHostKey } from '../tours/types';

/**
 * What a collector saw (critic C6). PRIVACY: a signal holds only a type, the
 * section key (from the pathname), the closest tour anchor id, an error CODE
 * and an opaque in-memory key for «the same form / the same button» — never
 * field values, DOM text, titles (a drawer's title carries a patient's name)
 * or record ids. Nothing of it leaves the browser; only the eventual
 * `OFFERED` event does, and that carries a tour key.
 */
export type StruggleSignal =
  | { type: 'api-error'; section: string; code: string; anchor: string | null }
  | { type: 'validation'; section: string; form: string; anchor: string | null }
  | { type: 'blocked-press'; section: string; target: string; anchor: string | null }
  | { type: 'form-abandon'; section: string; host: InlineHostKey; anchor: string | null };

export type StruggleKind = StruggleSignal['type'];

/** A tripped threshold: someone seems stuck in `context`. */
export interface StruggleTrip {
  kind: StruggleKind;
  /** The backoff context: the section key, or the inline host. */
  context: string;
  section: string;
  /** The closest tour anchor of the last signal, for a step-level offer. */
  anchor: string | null;
  /** Set for a form-abandon: the inline host the offer belongs in. */
  host?: InlineHostKey;
  at: number;
}

interface Rule {
  windowMs: number;
  count: number;
  /** Signals closer than this to the previous one of the same key are one. */
  mergeMs?: number;
}

const RULES: Record<StruggleKind, Rule> = {
  'api-error': { windowMs: STRUGGLE_API_ERROR_WINDOW_MS, count: STRUGGLE_API_ERROR_COUNT },
  validation: {
    windowMs: STRUGGLE_VALIDATION_WINDOW_MS,
    count: STRUGGLE_VALIDATION_COUNT,
    mergeMs: STRUGGLE_BURST_MS,
  },
  'blocked-press': { windowMs: STRUGGLE_BLOCKED_WINDOW_MS, count: STRUGGLE_BLOCKED_COUNT },
  'form-abandon': { windowMs: STRUGGLE_ABANDON_WINDOW_MS, count: STRUGGLE_ABANDON_COUNT },
};

function keyOf(signal: StruggleSignal): string {
  switch (signal.type) {
    case 'api-error':
      return `api|${signal.section}|${signal.code}`;
    case 'validation':
      return `val|${signal.section}|${signal.form}`;
    case 'blocked-press':
      return `blk|${signal.section}|${signal.target}`;
    case 'form-abandon':
      return `abn|${signal.host}`;
  }
}

export interface StruggleScorer {
  /** Counts a signal; returns the trip when this one crossed its threshold. */
  push(signal: StruggleSignal, now: number): StruggleTrip | null;
  /**
   * A success (a saved form, a finished tour): the page's counts start
   * afresh. An inline form's abandons are cleared only by using THAT form
   * (`host`) — an unrelated save elsewhere says nothing about it.
   */
  reset(scope?: { host: InlineHostKey }): void;
  /** Everything forgotten, every form's abandons included (a clinic switch, tests). */
  clear(): void;
}

/**
 * Pure and in-memory: a sliding window per «same thing» key. A key that trips
 * starts over, so one stuck moment is one offer, not three.
 */
export function createStruggleScorer(): StruggleScorer {
  let events = new Map<string, number[]>();

  return {
    push(signal, now) {
      const rule = RULES[signal.type];
      const key = keyOf(signal);
      const times = (events.get(key) ?? []).filter((t) => now - t < rule.windowMs);
      const last = times[times.length - 1];
      if (rule.mergeMs !== undefined && last !== undefined && now - last < rule.mergeMs) {
        events.set(key, times);
        return null;
      }
      times.push(now);
      if (times.length < rule.count) {
        events.set(key, times);
        return null;
      }
      events.delete(key);
      return {
        kind: signal.type,
        context: signal.type === 'form-abandon' ? signal.host : signal.section,
        section: signal.section,
        anchor: signal.anchor,
        ...(signal.type === 'form-abandon' ? { host: signal.host } : {}),
        at: now,
      };
    },
    reset(scope) {
      if (scope) {
        events.delete(`abn|${scope.host}`);
        return;
      }
      events = new Map([...events].filter(([key]) => key.startsWith('abn|')));
    },
    clear() {
      events = new Map();
    },
  };
}
